import Image from 'next/image';
import { BookRecommendationTranslations } from '@/lib/translations/types';

interface BookRecommendationProps {
  className?: string;
  translations: BookRecommendationTranslations;
  bookData: {
    title: string;
    imageUrl: string;
    amazonUrl: string;
    affiliateSource: string;
    rating?: number;
  };
  onBookClick?: () => void;
}

export default function BookRecommendation({ 
  className = '', 
  translations, 
  bookData,
  onBookClick
}: BookRecommendationProps) {
  // Use provided book data or fallback to default
  const book = bookData;

  if (!book) return null;

  // Book data comes directly from props, no translations needed

  // Star rating component
  const StarRating = ({ rating, maxStars = 5 }: { rating: number; maxStars?: number }) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    
    // Full stars
    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <svg key={`full-${i}`} className="w-3 h-3 text-yellow-400 fill-current" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      );
    }
    
    // Half star
    if (hasHalfStar) {
      stars.push(
        <div key="half" className="relative w-3 h-3">
          <svg className="w-3 h-3 text-gray-300 fill-current" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
          <svg className="absolute top-0 left-0 w-3 h-3 text-yellow-400 fill-current overflow-hidden" style={{ width: '50%' }} viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        </div>
      );
    }
    
    // Empty stars
    const emptyStars = maxStars - fullStars - (hasHalfStar ? 1 : 0);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <svg key={`empty-${i}`} className="w-3 h-3 text-gray-300 fill-current" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      );
    }
    
    return (
      <div className="flex items-center gap-1 mt-[4px]">
        {stars}
      </div>
    );
  };


  return (
    <article className={`bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden ${className}`}>
      {/* Title */}
      <div className="bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 border-b border-purple-300 px-4 py-3">
        <h3 className="text-sm font-semibold text-white text-center drop-shadow-sm">
          {translations.title}
        </h3>
      </div>
      
      {/* Content */}
      <div className="p-3">
      
      <div className="flex items-start gap-3">
        {/* Book Cover */}
        <div className="flex-shrink-0">
          <Image
            src={book.imageUrl}
            title={book.title}
            alt={`${book.title} book cover`}
            width={60}
            height={80}
            className="rounded-md shadow-sm"
            priority={false}
            loading="lazy"
          />
        </div>

        {/* Book Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap border-l-2 border-blue-600 pl-2 mb-2">
            <h4 className="text-xs font-medium text-gray-800 leading-tight">
              {book.title}
            </h4>
            {book.rating && <StarRating rating={book.rating} />}
          </div>
          
          <a
            href={book.amazonUrl}
            target="_blank"
            rel="noopener nofollow sponsored"
            onClick={onBookClick}
            className="inline-block bg-blue-600 hover:bg-blue-700 text-white hover:text-white text-xs font-medium py-2 px-3 rounded-md transition-colors duration-200 text-center w-full"
            aria-label={`${translations.buyButton} - ${book.title}`}
            title={`${translations.buyButton} - ${book.title}`}
          >
            {translations.buyButton} {book.affiliateSource}
          </a>
        </div>
      </div>
      </div>
    </article>
  );
}
